import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, Inbox, FileSearch, Clock, CheckCircle2, X, Download, StickyNote, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/SiteLayout";
import { AssessmentPanel } from "../components/AssessmentPanel";
import { VerificationPanel } from "../components/VerificationPanel";

const STATUS_META = {
  new: { label: "New", cls: "bg-[#F4D387]/40 text-[#8a6d1f]" },
  contacted: { label: "Contacted", cls: "bg-blue-50 text-blue-700" },
  in_review: { label: "In Review", cls: "bg-[#C1A74A]/20 text-[#8a6d1f]" },
  completed: { label: "Completed", cls: "bg-[#1D504C]/10 text-[#1D504C]" },
  closed: { label: "Closed", cls: "bg-gray-100 text-gray-500" },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`tt-status-badge ${m.cls}`} data-testid={`status-badge-${status}`}>{m.label}</span>;
};

const KIND_LABELS = { contact: "Contact", title_search: "Title Search", expert: "Expert" };

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const DetailRow = ({ label, value }) =>
  value ? (
    <div className="py-3 border-b border-[#113835]/8 grid grid-cols-3 gap-4">
      <span className="tt-label !mb-0 col-span-1">{label}</span>
      <span className="text-sm text-[#113835] col-span-2 break-words">{value}</span>
    </div>
  ) : null;

const StatusButtons = ({ current, onSet }) => (
  <div className="flex flex-wrap gap-2" data-testid="detail-status-buttons">
    {Object.entries(STATUS_META).map(([key, m]) => (
      <button
        key={key}
        data-testid={`detail-set-status-${key}`}
        onClick={() => onSet(key)}
        className={`tt-status-badge border transition-colors ${current === key ? "border-[#113835] " + m.cls : "border-[#113835]/15 text-gray-500 hover:border-[#C1A74A]"}`}
      >
        {m.label}
      </button>
    ))}
  </div>
);

const DocumentsList = ({ documents, onDownload }) => (
  <div>
    <p className="tt-label mb-3">Uploaded Documents</p>
    <ul className="space-y-2" data-testid="detail-documents">
      {documents.map((d) => (
        <li key={d.id} className="flex items-center justify-between border border-[#113835]/10 px-4 py-3 text-sm">
          <span className="text-[#113835] truncate pr-4">{d.original_filename}</span>
          <button data-testid={`detail-download-${d.id}`} onClick={() => onDownload(d)} className="text-[#C1A74A] hover:text-[#113835] transition-colors shrink-0" aria-label={`Download ${d.original_filename}`}>
            <Download size={16} strokeWidth={1.5} />
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const NotesSection = ({ notes, note, setNote, saving, onSubmit }) => (
  <div>
    <p className="tt-label mb-3 flex items-center gap-2"><StickyNote size={14} strokeWidth={1.25} /> Internal Notes</p>
    <div className="space-y-3 mb-4" data-testid="detail-notes-list">
      {notes.length === 0 && <p className="text-xs text-gray-400 font-light">No notes yet.</p>}
      {notes.map((n) => (
        <div key={n.id} className="bg-[#F9FAFA] border border-[#113835]/8 px-4 py-3">
          <p className="text-sm text-[#113835]">{n.text}</p>
          <p className="text-[0.65rem] text-gray-400 mt-2 uppercase tracking-wider">{n.author} · {fmtDate(n.created_at)}</p>
        </div>
      ))}
    </div>
    <form onSubmit={onSubmit} className="flex gap-3">
      <input data-testid="detail-note-input" className="tt-input flex-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note..." />
      <button data-testid="detail-note-submit" type="submit" disabled={saving || !note.trim()} className="tt-btn-primary !py-2 !px-5 disabled:opacity-50">Add</button>
    </form>
  </div>
);

function DetailPanel({ id, onClose, onChanged }) {
  const [item, setItem] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/inquiries/${id}`);
    setItem(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/admin/inquiries/${id}/status`, { status });
      toast.success(`Status updated to ${STATUS_META[status].label}`);
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/inquiries/${id}/notes`, { note });
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/admin/documents/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download document");
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="inquiry-detail-panel">
      <div className="flex-1 bg-[#113835]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#113835] text-white px-8 py-6 flex items-center justify-between z-10">
          <div>
            <p className="tt-eyebrow !text-[0.62rem]">{KIND_LABELS[item.kind] || "Inquiry"} {item.kind === "expert" ? "Application" : item.kind === "contact" ? "Inquiry" : "Request"}</p>
            <h2 className="font-heading text-xl font-medium mt-1" data-testid="detail-name">{item.full_name}</h2>
          </div>
          <button data-testid="detail-close" onClick={onClose} aria-label="Close" className="p-2 hover:text-[#F4D387] transition-colors"><X strokeWidth={1.25} /></button>
        </div>
        <div className="px-8 py-8 space-y-10">
          <div>
            <p className="tt-label mb-3">Status</p>
            <StatusButtons current={item.status} onSet={updateStatus} />
          </div>

          <div>
            <p className="tt-label mb-2">Details</p>
            <DetailRow label="Email" value={item.email} />
            <DetailRow label="Phone" value={item.phone} />
            <DetailRow label="Company" value={item.company} />
            <DetailRow label="Received" value={fmtDate(item.created_at)} />
            {item.kind === "contact" && (
              <>
                <DetailRow label="Subject" value={item.subject} />
                <DetailRow label="Message" value={item.message} />
              </>
            )}
            {item.kind === "expert" && (
              <>
                <DetailRow label="Profession" value={item.profession} />
                <DetailRow label="License / Bar No." value={item.license_number} />
                <DetailRow label="Experience" value={item.experience_years ? `${item.experience_years} years` : ""} />
                <DetailRow label="City" value={item.city} />
                <DetailRow label="State" value={item.state} />
                <DetailRow label="Districts Covered" value={item.districts_covered} />
                <DetailRow label="About Practice" value={item.message} />
              </>
            )}
            {item.kind === "title_search" && (
              <>
                <DetailRow label="Client Type" value={item.client_type} />
                <DetailRow label="Property Address" value={item.property_address} />
                <DetailRow label="City" value={item.city} />
                <DetailRow label="State" value={item.state} />
                <DetailRow label="Parcel / Survey No." value={item.parcel_number} />
                <DetailRow label="Property Type" value={item.property_type} />
                <DetailRow label="Service Needed" value={item.service_needed} />
                <DetailRow label="Purpose" value={item.purpose} />
                <DetailRow label="Turnaround" value={item.turnaround} />
                <DetailRow label="Notes" value={item.additional_notes} />
              </>
            )}
          </div>

          {item.documents?.length > 0 && <DocumentsList documents={item.documents} onDownload={downloadDoc} />}

          {item.kind === "title_search" && <VerificationPanel item={item} onSaved={load} />}

          {item.kind === "title_search" && <AssessmentPanel item={item} onSaved={load} />}

          <NotesSection notes={item.notes || []} note={note} setNote={setNote} saving={saving} onSubmit={addNote} />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (status) params.status = status;
      if (search) params.search = search;
      const [m, list] = await Promise.all([api.get("/admin/metrics"), api.get("/admin/inquiries", { params })]);
      setMetrics(m.data);
      setItems(list.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [kind, status, search, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (status) params.status = status;
      const res = await api.get("/admin/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "terraa-title-inquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin/login");
    }
  };

  const renderRows = () => {
    if (loading) {
      return <tr><td colSpan={5} className="px-8 py-16 text-center text-sm text-gray-400 font-light">Loading inquiries...</td></tr>;
    }
    if (items.length === 0) {
      return <tr><td colSpan={5} data-testid="admin-empty-state" className="px-8 py-16 text-center text-sm text-gray-400 font-light">No inquiries found.</td></tr>;
    }
    return items.map((it) => (
      <tr
        key={it.id}
        data-testid={`inquiry-row-${it.id}`}
        onClick={() => setSelected(it.id)}
        className="border-b border-[#113835]/8 hover:bg-[#F9FAFA] cursor-pointer transition-colors"
      >
        <td className="px-8 py-5">
          <p className="font-heading text-sm font-medium text-[#113835]">{it.full_name}</p>
          {it.company && <p className="text-xs text-gray-400 mt-0.5">{it.company}</p>}
        </td>
        <td className="px-8 py-5 text-sm text-gray-600">{KIND_LABELS[it.kind] || it.kind}</td>
        <td className="px-8 py-5">
          <p className="text-sm text-gray-600">{it.email}</p>
          <p className="text-xs text-gray-400 mt-0.5">{it.phone}</p>
        </td>
        <td className="px-8 py-5 text-sm text-gray-600 whitespace-nowrap">{fmtDate(it.created_at)}</td>
        <td className="px-8 py-5"><StatusBadge status={it.status} /></td>
      </tr>
    ));
  };

  const cards = metrics
    ? [
        { label: "Total Inquiries", value: metrics.total, icon: Inbox },
        { label: "New Requests", value: metrics.by_status.new, icon: FileSearch },
        { label: "In Review", value: metrics.by_status.in_review, icon: Clock },
        { label: "Completed", value: metrics.by_status.completed, icon: CheckCircle2 },
      ]
    : [];

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#F9FAFA]">
      <header className="bg-[#113835] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo dark />
            <span className="hidden sm:block text-[0.65rem] tracking-[0.28em] uppercase text-white/50 border-l border-white/20 pl-6">Admin Dashboard</span>
          </div>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-sm text-white/80 hover:text-[#F4D387] transition-colors">
            <LogOut size={16} strokeWidth={1.25} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#113835]/10 border border-[#113835]/10" data-testid="admin-metrics-cards">
          {cards.map((c) => (
            <div key={c.label} className="bg-white p-8" data-testid={`metric-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
              <c.icon size={22} strokeWidth={1.25} className="text-[#C1A74A]" />
              <p className="font-heading text-4xl font-semibold text-[#113835] mt-4">{c.value}</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-2">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#113835]/10">
          <div className="px-8 py-6 border-b border-[#113835]/10 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
            <h2 className="font-heading text-lg font-medium text-[#113835]">Inquiries</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <Search size={15} strokeWidth={1.25} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#C1A74A]" />
                <input data-testid="admin-search-input" className="tt-input !py-2 !pl-6 w-full sm:w-56" placeholder="Search name, email, phone" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <select data-testid="admin-filter-kind" className="tt-input !py-2 w-full sm:w-44" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">All Types</option>
                <option value="contact">Contact Inquiries</option>
                <option value="title_search">Title Search Requests</option>
                <option value="expert">Expert Applications</option>
              </select>
              <select data-testid="admin-filter-status" className="tt-input !py-2 w-full sm:w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
              <button data-testid="admin-export-csv" onClick={exportCsv} className="tt-btn-outline !py-2 !px-5 whitespace-nowrap">
                <FileDown size={15} strokeWidth={1.5} /> Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-inquiries-table">
              <thead>
                <tr className="border-b border-[#113835]/10">
                  {["Name", "Type", "Contact", "Received", "Status"].map((h) => (
                    <th key={h} className="px-8 py-4 font-heading text-[0.68rem] uppercase tracking-[0.16em] text-[#1D504C] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        </div>
      </main>

      {selected && <DetailPanel id={selected} onClose={() => setSelected(null)} onChanged={loadAll} />}
    </div>
  );
}
